import { Component, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RepresentanteAgenteService } from './services/representanteAgente.service';
import { representanteAegente } from './interface/representanteAgente.interface';

import { ViewRepresentanteComponent } from './utils/view-representante-succes';
import Swal from 'sweetalert2';
import { CARGOS_EMPRESA } from './utils/cargos-empresa';

/**
 * Componente encargado de gestionar la edición de un representante agente vigilado.
 *
 * Permite:
 * - Cargar la información de un representante desde el backend (Keycloak).
 * - Mostrar un formulario con validaciones.
 * - Guardar los cambios realizados en el representante.
 *  * @author Carlos Eduardo Riaño
 * @date 2025-10-01
 */

@Component({
    selector: 'app-representante-agente-vigilado',
    templateUrl: './representante-agente-vigilado.component.html',
    styleUrls: ['./representante-agente-vigilado.component.css'],
})
export class RepresentanteAgenteVigiladoComponent implements OnInit {
    constructor(
        private readonly fb: FormBuilder,
        private readonly route: ActivatedRoute,
        private readonly raService: RepresentanteAgenteService,
        private readonly router: Router
    ) {}

    usuarioForm: FormGroup;
    userId: string;
    cargosEmpresa: string[] = CARGOS_EMPRESA;
    @ViewChild('viewRepresentanteSucces', { static: false })
    viewSucces: ViewRepresentanteComponent;
    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.userId = params['id'];
        });

        this.usuarioForm = this.fb.group({
            username: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[a-zA-Z0-9]{3,255}$/),
                ],
            ],
            firstName: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[a-zA-ZÀ-Öà-ö\s]+$/),
                ],
            ],
            lastName: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[a-zA-ZÀ-Öà-ö\s]+$/),
                ],
            ],
            cargo: ['', [Validators.required]],
            telefono: [
                '',
                [Validators.required, Validators.pattern(/^\d{10}$/)],
            ],
            email: ['', [Validators.required, Validators.email]],
        });
        this.obtenerRepresentante();
    }

    /**
     * Obtiene los datos del representante desde el servicio
     * y los asigna al formulario.
     *
     * @returns {Promise<void>}
     */
    async obtenerRepresentante(): Promise<void> {
        try {
            const representante = await this.raService.getRepresentanteAgente(
                `representante-agente-vigilado/Keycloack/${this.userId}`
            );
            this.usuarioForm.patchValue({
                username: representante?.username,
                firstName: representante.nombre,
                lastName: representante?.apellido,
                cargo: representante.cargo,
                telefono: representante.telefono,
                email: representante.email,
            });
        } catch (error) {
            console.error('Error al obtener el representante:', error);
            Swal.fire(
                'Error',
                'No se pudo obtener la información del representante.',
                'error'
            );
        }
    }

    /**
     * Guarda los cambios del usuario en Keycloak y actualiza los datos del representante.
     * Si la operación es exitosa, reinicia la sesión y redirige al inicio.
     *
     * @returns {Promise<void>}
     */

    async guardarUsuario(): Promise<void> {
        try {
            if (this.usuarioForm.valid) {
                const usuario: representanteAegente = {
                    id: this.userId,
                    username: this.usuarioForm.get('username').value,
                    firstName: this.usuarioForm.get('firstName').value,
                    lastName: this.usuarioForm.get('lastName').value,
                    email: this.usuarioForm.get('email').value,
                    attributes: {
                        cargo: [this.usuarioForm.get('cargo').value],
                        telefono: [this.usuarioForm.get('telefono').value],
                    },
                };
                await this.raService.putRepresentanteAgente(`usuario`, usuario);
                Swal.fire(
                    '',
                    'Representante actualizado con éxito, se reiniciará la sesión',
                    'success'
                ).then((result) => {
                    if (result.isConfirmed) {
                        this.router.navigate(['/inicio']);
                    }
                });
            } else {
                this.usuarioForm.markAllAsTouched();
                console.log('Ocurrió un error en el formulario');
            }
        } catch (error) {
            console.error('Error al actualizar el usuario:', error);
        }
    }
}
